import { LoadingOutlined } from "@ant-design/icons"
import { Spin } from "antd"
import { SkeletonLines } from "components/new-stuff/ui/Skeleton"

interface LoadingIndicatorProps {
  spinning?: boolean
  className?: string
}

export const LoadingIndicator = ({
  spinning = true,
  className = "",
}: LoadingIndicatorProps) => {
  const antIcon = (
    <LoadingOutlined
      style={{ fontSize: 24 }}
      spin={spinning}
    />
  )
  return (
    <Spin
      indicator={antIcon}
      className={className}
    />
  )
}

type LoadingProps = React.ComponentProps<typeof Spin> & {
  /** Placeholder bars to draw. Raise it where the region is tall. */
  skeletonLines?: number
  /**
   * Render the children anyway, hidden behind the placeholder.
   *
   * Needed wherever a child must exist before the data does - something that
   * attaches a ref and drives an imperative library through it, or holds state
   * that must survive the wait. Without this the child mounts only after
   * loading ends, and any effect that reached for `ref.current` in the
   * meantime found null.
   */
  keepMounted?: boolean
  /**
   * What to draw while loading, in place of the default lines. Pass a shaped
   * placeholder (FormSkeleton, CardListSkeleton, ProfileSkeleton, a table's own
   * skeleton) so the handover to the loaded content does not jump.
   */
  placeholder?: React.ReactNode
}

/**
 * A region waiting on content.
 *
 * It used to spin over an empty frame, which told the user nothing about what
 * was coming or how much of it. Nearly every caller wraps a region waiting on
 * a query, so it draws a placeholder of that region's shape instead.
 *
 * Note it does not render its children while loading. That is deliberate - a
 * form gets to mount with the values it is waiting for, rather than mounting
 * empty and being told about them later - but it does mean child state does
 * not survive the wait. Where content is already on screen and only an
 * outcome is pending, `BusyOverlay` is the right component.
 */
export const LoadingRegion = ({
  spinning = true,
  skeletonLines,
  keepMounted = false,
  placeholder: shapedPlaceholder,
  children,
  className = "",
  ...rest
}: LoadingProps) => {
  if (!spinning) return <>{children}</>

  // A wrapped region is usually a panel of several fields; a bare one is
  // typically a short standalone block.
  const placeholder = shapedPlaceholder ?? (
    <SkeletonLines lines={skeletonLines ?? (children ? 4 : 3)} />
  )

  if (keepMounted) {
    return (
      <div
        className={`relative w-full ${className}`}
        aria-busy="true"
      >
        {/* `invisible` rather than unmounted: the child keeps its layout box,
            so it measures correctly and its ref is live from the first
            render. */}
        <div
          className="invisible"
          aria-hidden="true"
        >
          {children}
        </div>
        <div className="absolute inset-0 bg-white p-4">{placeholder}</div>
      </div>
    )
  }

  return (
    <div
      className={`w-full ${className}`}
      aria-busy="true"
    >
      {placeholder}
    </div>
  )
}

/**
 * An action the user started, still in flight.
 *
 * The content underneath is already on screen and correct - only the outcome
 * is pending - so it stays put and dims behind a spinner. Replacing it with a
 * placeholder would claim the content is unknown, which it is not.
 */
export const BusyOverlay = ({
  spinning = false,
  children,
  className = "",
  ...rest
}: React.ComponentProps<typeof Spin>) => (
  <Spin
    spinning={spinning}
    className={className}
    {...rest}
  >
    {children}
  </Spin>
)

/**
 * @deprecated The name says spinner but the behaviour is a placeholder.
 * Use `LoadingRegion` for content on its way in, or `BusyOverlay` for an
 * action in flight. Kept so the call sites can move a module at a time.
 */
export const CustomSpin = LoadingRegion

type props = {
  children: JSX.Element | JSX.Element[]
  message: string
  loading: "true" | "false"
}

export const Loader: React.FC<props> = ({ loading, children, message }) => {
  return (
    <>
      {loading === "true" ? (
        <div className="h-full w-full p-3 flex justify-center items-center gap-2">
          <LoadingIndicator spinning={true} />
          <span>{message}</span>
        </div>
      ) : (
        children
      )}
    </>
  )
}
